import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "../types";

/**
 * Side-effect bundle for the `lang` state owned by the shell:
 * - persists to localStorage
 * - mirrors to `<html lang>` + key SEO meta tags
 * - pulls/pushes `profiles.preferred_language` when signed in
 *
 * Kept as a hook so `NutriMart.tsx` no longer hosts 60+ lines of
 * unrelated SEO/profile glue.
 */
export function useLangSync(
  lang: Lang,
  userId: string | undefined,
  setLang: (l: Lang) => void,
) {
  // Persist + sync SEO meta tags whenever lang changes.
  useEffect(() => {
    try { window.localStorage.setItem("nm_lang", lang); } catch { /* ignore */ }
    try {
      const doc = document;
      doc.documentElement.lang = lang === "en" ? "en" : "es-DO";
      const setMeta = (sel: string, attr: string, value: string) => {
        const el = doc.querySelector(sel) as HTMLMetaElement | null;
        if (el) el.setAttribute(attr, value);
      };
      if (lang === "en") {
        doc.title = "NutriMarket — Supplements & Wellness DR";
        setMeta('meta[name="description"]', "content",
          "NutriMarket: marketplace for supplements, protein, vitamins and wellness with nationwide shipping in the Dominican Republic.");
        setMeta('meta[property="og:title"]', "content", "NutriMarket — Supplements & Wellness DR");
        setMeta('meta[property="og:description"]', "content",
          "Supplements, protein, vitamins and wellness with nationwide shipping in the Dominican Republic.");
        setMeta('meta[property="og:locale"]', "content", "en_US");
      } else {
        doc.title = "NutriMarket — Suplementos y Bienestar RD";
        setMeta('meta[name="description"]', "content",
          "NutriMart: marketplace de suplementos, proteína, vitaminas y bienestar con envío en toda República Dominicana.");
        setMeta('meta[property="og:title"]', "content", "NutriMarket — Suplementos y Bienestar RD");
        setMeta('meta[property="og:description"]', "content",
          "Suplementos, proteína, vitaminas y bienestar con envío en toda República Dominicana.");
        setMeta('meta[property="og:locale"]', "content", "es_DO");
      }
    } catch { /* never let SEO sync break the app */ }
  }, [lang]);

  // On login, prefer the stored profile language over the local default.
  useEffect(() => {
    let cancelled = false;
    if (!userId) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("preferred_language")
        .eq("user_id", userId)
        .maybeSingle();
      if (cancelled) return;
      const pl = (data as any)?.preferred_language;
      if (pl === "en" || pl === "es") setLang(pl);
    })();
    return () => { cancelled = true; };
  }, [userId, setLang]);

  // Push lang changes to the profile while signed in.
  useEffect(() => {
    if (!userId) return;
    supabase
      .from("profiles")
      .update({ preferred_language: lang })
      .eq("user_id", userId)
      .then(() => { /* best-effort */ });
  }, [lang, userId]);
}